'use client';

import React, { forwardRef, useCallback, useEffect, useRef, useState } from 'react';
import { animate, AnimatePresence, motion, Transition, useTime, useTransform } from 'motion/react';
import type { HTMLMotionProps } from 'motion/react';
import { Icon, IconProps } from '../../icons';
import './Button.css';

export type ButtonVariant = 'primary' | 'secondary' | 'transparent' | 'link';
export type ButtonSize = 'sm' | 'md' | 'lg';
export type ButtonState = 'idle' | 'processing' | 'success' | 'error';

export interface ButtonProps extends Omit<HTMLMotionProps<'button'>, 'ref'> {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leadingVisual?: IconProps['name'] | React.ReactNode;
  trailingVisual?: IconProps['name'] | React.ReactNode;
  children: React.ReactNode;
  loading?: boolean;
  // New multi-state props
  state?: ButtonState;
  onStateChange?: (state: ButtonState) => void;
  showStateBadge?: boolean;
  stateLabels?: {
    idle?: string;
    processing?: string;
    success?: string;
    error?: string;
  };
}

function isReactElement(node: unknown): node is React.ReactElement {
  return React.isValidElement(node);
}

// Icon components for different states
const ICON_SIZE = 20;
const STROKE_WIDTH = 1.5;
const VIEW_BOX_SIZE = 24;

const svgProps = {
  width: ICON_SIZE,
  height: ICON_SIZE,
  viewBox: `0 0 ${VIEW_BOX_SIZE} ${VIEW_BOX_SIZE}`,
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: STROKE_WIDTH,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
};

const springConfig: Transition = {
  type: 'spring',
  stiffness: 150,
  damping: 20,
};

const animations = {
  initial: { pathLength: 0 },
  animate: { pathLength: 1 },
  transition: springConfig,
};

const secondLineAnimation = {
  ...animations,
  transition: { ...springConfig, delay: 0.1 },
};

// Visually hidden utility style for screen-reader-only content
const srOnlyStyle: React.CSSProperties = {
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0, 0, 0, 0)',
  whiteSpace: 'nowrap',
  border: 0,
};

function usePrefersReducedMotion(): boolean {
  const [prefersReduced, setPrefersReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const update = () => setPrefersReduced(mediaQuery.matches);
    update();
    mediaQuery.addEventListener?.('change', update);
    return () => mediaQuery.removeEventListener?.('change', update);
  }, []);
  return prefersReduced;
}

function CheckIcon({ prefersReduced }: { prefersReduced: boolean }) {
  return (
    <motion.svg {...svgProps}>
      <motion.polyline
        points="4 12 9 17 20 6"
        initial={prefersReduced ? false : animations.initial}
        animate={prefersReduced ? undefined : animations.animate}
        transition={prefersReduced ? { duration: 0 } : animations.transition}
      />
    </motion.svg>
  );
}

function LoaderIcon({ prefersReduced }: { prefersReduced: boolean }) {
  const time = useTime();
  const rotate = useTransform(time, [0, 1000], [0, 360], { clamp: false });

  return (
    <motion.div
      style={{
        rotate: prefersReduced ? undefined : rotate,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: ICON_SIZE,
        height: ICON_SIZE,
        willChange: prefersReduced ? undefined : 'transform',
      }}
    >
      <motion.svg {...svgProps}>
        <motion.path
          d="M21 12a9 9 0 1 1-6.219-8.56"
          initial={prefersReduced ? false : animations.initial}
          animate={prefersReduced ? undefined : animations.animate}
          transition={prefersReduced ? { duration: 0 } : animations.transition}
        />
      </motion.svg>
    </motion.div>
  );
}

function XIcon({ prefersReduced }: { prefersReduced: boolean }) {
  return (
    <motion.svg {...svgProps}>
      <motion.line
        x1="6"
        y1="6"
        x2="18"
        y2="18"
        initial={prefersReduced ? false : animations.initial}
        animate={prefersReduced ? undefined : animations.animate}
        transition={prefersReduced ? { duration: 0 } : animations.transition}
      />
      <motion.line
        x1="18"
        y1="6"
        x2="6"
        y2="18"
        initial={prefersReduced ? false : secondLineAnimation.initial}
        animate={prefersReduced ? undefined : secondLineAnimation.animate}
        transition={prefersReduced ? { duration: 0 } : secondLineAnimation.transition}
      />
    </motion.svg>
  );
}

// State Badge Component
const StateBadge = ({
  state,
  labels,
  prefersReduced,
}: {
  state: ButtonState;
  labels: Required<NonNullable<ButtonProps['stateLabels']>>;
  prefersReduced: boolean;
}) => {
  const badgeRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!badgeRef.current) return;
    if (prefersReduced) return;

    if (state === 'error') {
      animate(
        badgeRef.current,
        { x: [0, -6, 6, -6, 0] },
        {
          duration: 0.3,
          ease: 'easeInOut',
          times: [0, 0.25, 0.5, 0.75, 1],
          repeat: 0,
          delay: 0.1,
        },
      );
    } else if (state === 'success') {
      animate(
        badgeRef.current,
        {
          scale: [1, 1.2, 1],
        },
        {
          duration: 0.3,
          ease: 'easeInOut',
          times: [0, 0.5, 1],
          repeat: 0,
        },
      );
    }
  }, [state, prefersReduced]);

  return (
    <motion.div
      ref={badgeRef}
      className="button__state-badge"
      style={{
        gap: state === 'idle' ? 0 : 8,
        willChange: prefersReduced ? undefined : 'transform, filter',
      }}
    >
      <StateIcon state={state} prefersReduced={prefersReduced} />
      <StateLabel state={state} labels={labels} />
    </motion.div>
  );
};

const StateIcon = ({ state, prefersReduced }: { state: ButtonState; prefersReduced: boolean }) => {
  let IconComponent = <></>;

  switch (state) {
    case 'idle':
      IconComponent = <></>;
      break;
    case 'processing':
      IconComponent = <LoaderIcon prefersReduced={prefersReduced} />;
      break;
    case 'success':
      IconComponent = <CheckIcon prefersReduced={prefersReduced} />;
      break;
    case 'error':
      IconComponent = <XIcon prefersReduced={prefersReduced} />;
      break;
  }

  return (
    <motion.span
      className="button__state-icon-container"
      animate={{
        width: state === 'idle' ? 0 : 20,
      }}
      transition={{
        type: 'spring',
        stiffness: 600,
        damping: 30,
      }}
    >
      <AnimatePresence initial={false}>
        <motion.span
          key={state}
          className="button__state-icon"
          initial={{
            y: -40,
            scale: 0.5,
            filter: 'blur(6px)',
          }}
          animate={{
            y: 0,
            scale: 1,
            filter: 'blur(0px)',
          }}
          exit={{
            y: 12,
            scale: 0.8,
            filter: 'blur(4px)',
            opacity: 0,
          }}
          transition={{
            duration: 0.15,
            ease: 'easeInOut',
          }}
          style={{ willChange: 'transform, filter' }}
        >
          {IconComponent}
        </motion.span>
      </AnimatePresence>
    </motion.span>
  );
};

const StateLabel = ({
  state,
  labels,
}: {
  state: ButtonState;
  labels: Required<NonNullable<ButtonProps['stateLabels']>>;
}) => {
  const [labelWidth, setLabelWidth] = useState(0);
  const measureRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (measureRef.current) {
      const { width } = measureRef.current.getBoundingClientRect();
      setLabelWidth(width);
    }
  }, [state, labels]);

  const currentLabel = labels[state];

  return (
    <>
      {/* Hidden copy of label to measure width */}
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
        }}
      >
        {currentLabel}
      </div>

      <motion.span
        layout
        style={{
          position: 'relative',
        }}
        animate={{
          width: labelWidth,
        }}
        transition={{
          type: 'spring',
          stiffness: 600,
          damping: 30,
        }}
      >
        <AnimatePresence mode="sync" initial={false}>
          <motion.div
            key={state}
            style={{
              whiteSpace: 'nowrap',
            }}
            initial={{
              y: -20,
              opacity: 0,
              filter: 'blur(10px)',
              position: 'absolute',
            }}
            animate={{
              y: 0,
              opacity: 1,
              filter: 'blur(0px)',
              position: 'relative',
            }}
            exit={{
              y: 8,
              opacity: 0,
              filter: 'blur(4px)',
              position: 'absolute',
            }}
            transition={{
              duration: 0.12,
              ease: [0.4, 0, 1, 1],
            }}
          >
            {currentLabel}
          </motion.div>
        </AnimatePresence>
      </motion.span>
    </>
  );
};

const ButtonInternal = (
  {
    variant = 'primary',
    size = 'md',
    leadingVisual,
    trailingVisual,
    children,
    disabled,
    loading = false,
    state = 'idle',
    showStateBadge = false,
    stateLabels,
    ...rest
  }: ButtonProps,
  ref: React.Ref<HTMLButtonElement>,
) => {
  const { onClick: userOnClick, className: userClassName, type: userType, ...buttonProps } = rest;
  const { onStateChange: _omitOnStateChange, ...sanitizedButtonProps } = buttonProps as {
    onStateChange?: (state: ButtonState) => void;
  } & Record<string, unknown>;
  void _omitOnStateChange;
  const isProcessing = loading || state === 'processing';
  const isDisabled = disabled || isProcessing;
  const prefersReduced = usePrefersReducedMotion();

  const defaultStateLabels = {
    idle: typeof children === 'string' ? children : 'Start',
    processing: 'Processing',
    success: 'Done',
    error: 'Something went wrong',
  };

  const finalStateLabels = { ...defaultStateLabels, ...stateLabels };

  const renderVisual = (
    visual: IconProps['name'] | React.ReactNode,
    position: 'leading' | 'trailing',
  ) => {
    if (!visual) return null;
    if (typeof visual === 'string') {
      // Map button sizes to icon pixel sizes
      const iconSizeMap: Record<ButtonSize, number> = { sm: 14, md: 14, lg: 18 };
      const iconSize = iconSizeMap[size];
      // Let icons inherit currentColor from the button for all variants
      const iconColor: IconProps['color'] | undefined = undefined;
      return (
        <Icon
          name={visual as IconProps['name']}
          size={iconSize}
          {...(iconColor ? { color: iconColor } : {})}
          className={`button__icon button__icon--${position}`}
          aria-hidden="true"
        />
      );
    }
    if (isReactElement(visual)) {
      const visualElement = visual as React.ReactElement<{ className?: string }>;
      return React.cloneElement(visualElement, {
        className:
          `button__icon button__icon--${position} ${visualElement.props.className || ''}`.trim(),
        'aria-hidden': true,
      } as Partial<typeof visualElement.props> & { 'aria-hidden': true });
    }
    if (process.env.NODE_ENV === 'development') {
      console.warn(`Invalid ${position}Visual prop provided to Button`);
    }
    return null;
  };

  const handleClick = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      // Intentionally do not modify state automatically; controlled by consumer
      userOnClick?.(e);
    },
    [userOnClick],
  );

  return (
    <motion.button
      ref={ref}
      type={userType ?? 'button'}
      className={`button${isDisabled ? ' button--disabled' : ''}${userClassName ? ` ${userClassName}` : ''}`}
      data-variant={variant}
      data-size={size}
      data-state={state}
      disabled={isDisabled}
      aria-busy={isProcessing || undefined}
      onClick={handleClick}
      whileHover={!isDisabled && !prefersReduced ? { scale: 1.03 } : undefined}
      whileTap={!isDisabled && !prefersReduced ? { scale: 0.97 } : undefined}
      transition={{ type: 'spring', stiffness: 600, damping: 35 }}
      style={{ willChange: !prefersReduced ? 'transform' : undefined }}
      {...(sanitizedButtonProps as typeof buttonProps)}
    >
      {showStateBadge ? (
        <>
          <StateBadge state={state} labels={finalStateLabels} prefersReduced={prefersReduced} />
          <span aria-live="polite" style={srOnlyStyle}>
            {finalStateLabels[state]}
          </span>
        </>
      ) : isProcessing ? (
        <>
          <span className="button__loader" aria-hidden>
            Loading...
          </span>
          <span aria-live="polite" style={srOnlyStyle}>
            {finalStateLabels.processing}
          </span>
        </>
      ) : (
        <span className="button__content">
          {renderVisual(leadingVisual, 'leading')}
          <span>{children}</span>
          {renderVisual(trailingVisual, 'trailing')}
        </span>
      )}
    </motion.button>
  );
};

export const Button = React.memo(forwardRef<HTMLButtonElement, ButtonProps>(ButtonInternal));
Button.displayName = 'Button';
